/**
 * processLibraryPanelPreferences.js — story 167 (HU-01, HU-02, HU-04 · RN-04, D16).
 * What the process and library panel remembers in the browser and the limits every size is held to.
 * Pure except for the two storage functions, the only place that touches localStorage.
 */
export const PANEL_PREFERENCES_KEY = 'mic.workflowGraph.processLibraryPanel'

export const PANEL_TAB = { PROCESS: 'PROCESS', MIXED: 'MIXED', LIBRARY: 'LIBRARY' }
export const LIBRARY_FAMILY = { PROCESSES: 'PROCESSES', OPERATIONS: 'OPERATIONS', TECHNOLOGIES: 'TECHNOLOGIES' }

export const PANEL_WIDTH = { MIN: 220, MAX: 560, DEFAULT: 272 }
export const LIBRARY_HEIGHT = { MIN: 160, MAX: 560 }
/** The thin strip that folds and unfolds the panel, the palette's one of before this story (PO, 28/09/2026). */
export const PANEL_EDGE_BUTTON_WIDTH = 24

export const RESIZE_AXIS = { WIDTH: 'WIDTH', SPLIT: 'SPLIT' }

/**
 * `libraryHeight` at null means "never resized": half of the panel. A remembered number, 0 included, is a size and is
 * held to its limits (RN-04.3, S35). The panel is always as tall as the canvas: its bottom handle went away because
 * nobody needed it (PO, 29/09/2026).
 */
export const DEFAULT_PANEL_PREFERENCES = {
  tab: PANEL_TAB.MIXED,
  open: true,
  width: PANEL_WIDTH.DEFAULT,
  libraryHeight: null,
  family: LIBRARY_FAMILY.PROCESSES
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value)
const oneOf = (value, allowed, fallback) => (Object.values(allowed).includes(value) ? value : fallback)

export const normalizePanelPreferences = (raw) => {
  const stored = raw && typeof raw === 'object' ? raw : {}
  return {
    tab: oneOf(stored.tab, PANEL_TAB, DEFAULT_PANEL_PREFERENCES.tab),
    open: typeof stored.open === 'boolean' ? stored.open : DEFAULT_PANEL_PREFERENCES.open,
    width: isFiniteNumber(stored.width) ? Math.round(clamp(stored.width, PANEL_WIDTH.MIN, PANEL_WIDTH.MAX)) : PANEL_WIDTH.DEFAULT,
    libraryHeight: isFiniteNumber(stored.libraryHeight) ? Math.round(clamp(stored.libraryHeight, LIBRARY_HEIGHT.MIN, LIBRARY_HEIGHT.MAX)) : null,
    family: oneOf(stored.family, LIBRARY_FAMILY, DEFAULT_PANEL_PREFERENCES.family)
  }
}

export const readPanelPreferences = () => {
  try {
    return normalizePanelPreferences(JSON.parse(window.localStorage.getItem(PANEL_PREFERENCES_KEY) || '{}'))
  } catch (thrown) {
    return DEFAULT_PANEL_PREFERENCES
  }
}

export const writePanelPreferences = (preferences) => {
  try {
    window.localStorage.setItem(PANEL_PREFERENCES_KEY, JSON.stringify(preferences))
  } catch (thrown) {
    // A private window or a full quota: the panel keeps working and simply forgets on reload (RN-04.3).
  }
}

/** RN-02.2: the library zone of Mixed, 160-560 px, half of the panel until the user moves the bar. */
export const libraryHeightOf = (preferences, panelHeight) => {
  const requested = preferences.libraryHeight > 0 ? preferences.libraryHeight : Math.round(panelHeight / 2)
  return clamp(requested, LIBRARY_HEIGHT.MIN, LIBRARY_HEIGHT.MAX)
}

/** RN-04.5: Operations without the permission opens Processes, and what is remembered is not touched. */
export const effectiveLibraryFamily = (family, canReadOperationLibrary) =>
  family === LIBRARY_FAMILY.OPERATIONS && !canReadOperationLibrary ? LIBRARY_FAMILY.PROCESSES : family

/**
 * The size a drag of one of the two handles leaves, from where the drag started (`start`: the pointer and
 * the sizes at that moment) to where the pointer is now.
 */
export const resizedPreferences = (axis, start, point) => {
  if (axis === RESIZE_AXIS.WIDTH) {
    return { width: Math.round(clamp(start.width + point.x - start.x, PANEL_WIDTH.MIN, PANEL_WIDTH.MAX)) }
  }
  return { libraryHeight: Math.round(clamp(start.libraryHeight - (point.y - start.y), LIBRARY_HEIGHT.MIN, LIBRARY_HEIGHT.MAX)) }
}
